import { showToast } from '@components/utils/helper';
import { useIsMobile } from '@hooks/useIsMobile';
import { type ChangeEvent, type DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { type IActions } from '../../../interface/IAction';
import { type IKYCFaceEnrolResponse, type IKYCIdCheckResponse, type KYCReviewReason } from '../../../interface/IAgents';
import { type IPersonalDetailsSchema } from '../../pages/agent-application/create/helper';
import { inferMimeFromExtension, normaliseCardImage, normaliseSelfieImage, normaliseSelfieVideo } from '../media';
import CameraCaptureModal from './CameraCaptureModal';
import {
	CameraIcon,
	CheckIcon,
	FaceSilhouettePlaceholder,
	ReplaceIcon,
	RetryIcon,
	ScanCardIcon,
	SpinnerIcon,
	UploadIcon,
	VoterCardPlaceholder,
} from './icons';
import {
	FooterNote,
	ReconcileCallout,
	ResultChip,
	SkipLink,
	SlotActionButton,
	SlotActionRow,
	SlotSpinner,
	SlotThumb,
	VerificationCard,
	VerificationHeader,
	VerificationSlot,
} from './styled';

type SlotStatus = 'idle' | 'converting' | 'busy' | 'success' | 'warning' | 'failed';

// The subset of the Step-1 schema this card actually reads (`watch`) and writes
// (`setValue`). Any form whose values are a superset of these fields can host
// the card — the application wizard and the trusted-agent invitee form both do.
export type IdentityVerificationFields = Pick<
	IPersonalDetailsSchema,
	| 'profilePhoto'
	| 'profileUrl'
	| 'faceId'
	| 'votersCardDocument'
	| 'votersCardImage'
	| 'votersCardRegistrationNumber'
	| 'kycReviewRequired'
	| 'kycReviewReasons'
>;

interface IIdentityVerificationCardProps<TFieldValues extends IdentityVerificationFields> {
	hookForm: UseFormReturn<TFieldValues>;
	actions: IActions | undefined;
	// Observers don't submit a voter's card, so the card slot is hidden for them.
	showVoterCard?: boolean;
}

// Wide accept-list driven by what real mobile cameras / galleries produce.
// HEIC/HEIF (iOS) is converted to JPEG client-side via heic2any before any
// upload reaches KYC or media-manager. The browser also occasionally hands
// us a File with empty `type` for HEIC paths — we accept those too as long
// as the file extension is recognised.
const ALLOWED_SELFIE_IMAGE_MIME = [
	'image/jpeg',
	'image/jpg',
	'image/png',
	'image/webp',
	'image/avif',
	'image/heic',
	'image/heif',
];
const ALLOWED_SELFIE_VIDEO_MIME = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/3gpp', 'video/3gpp2'];
const ALLOWED_CARD_MIME = [...ALLOWED_SELFIE_IMAGE_MIME];
const MAX_IMAGE_SIZE = 8 * 1024 * 1024; // pre-compression cap; we shrink to ≤4MB before upload
const MAX_VIDEO_SIZE = 30 * 1024 * 1024;

// `accept` strings — galleries on iOS Files / older Android show a wider set
// when extensions are listed alongside MIME types.
const SELFIE_GALLERY_ACCEPT =
	'image/png,image/jpeg,image/jpg,image/webp,image/avif,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/webm,video/3gpp,.mov,.mp4,.webm,.3gp';
const SELFIE_CAMERA_ACCEPT = 'image/*,video/*';
const SELFIE_PHOTO_ONLY_ACCEPT = 'image/*';
const CARD_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp,image/avif,image/heic,image/heif,.heic,.heif';

// Hard rejection threshold for every confidence value KYC returns.
// The service is documented as returning percentages (0–100); values that
// look like a 0–1 ratio are normalised before comparison.
const MIN_CONFIDENCE_PERCENT = 90;
const toPercent = (v: number | null | undefined): number | null => {
	if (v == null || Number.isNaN(v)) return null;
	return v <= 1 ? v * 100 : v;
};

// INEC VIN is 19 alphanumeric characters. The OCR service prints it grouped
// with spaces (e.g. "90F5 B214 8B29 5880 624") and its own `vin_format_valid`
// flag fails on those spaces, so we normalise + validate the VIN ourselves
// rather than trusting that flag.
const VIN_FORMAT = /^[A-Z0-9]{19}$/;
const normaliseVin = (v: string | null | undefined): string => (v ?? '').replace(/\s+/g, '').toUpperCase();
const isVinFormatValid = (v: string): boolean => VIN_FORMAT.test(v);

// Resolve effective MIME — fall back to extension when the browser hands us
// an empty `file.type` (a known iOS Safari quirk for HEIC paths).
const effectiveMime = (file: File): string => file.type || inferMimeFromExtension(file.name) || '';

const isVideoFile = (file: File): boolean => effectiveMime(file).startsWith('video/');
const isImageFile = (file: File): boolean => effectiveMime(file).startsWith('image/');

const validateSelfie = (file: File): string | null => {
	const mime = effectiveMime(file);
	if (isVideoFile(file)) {
		if (mime && !ALLOWED_SELFIE_VIDEO_MIME.includes(mime)) {
			return 'That video format isn’t supported. Try MP4, MOV, WebM, or 3GP.';
		}
		if (file.size > MAX_VIDEO_SIZE) return 'Video must be smaller than 30 MB.';
		return null;
	}
	if (!isImageFile(file) || !ALLOWED_SELFIE_IMAGE_MIME.includes(mime)) {
		return 'Use a photo (JPG, PNG, HEIC, WebP) or a short MP4/MOV video.';
	}
	if (file.size > MAX_IMAGE_SIZE) return 'Photo must be smaller than 8 MB.';
	return null;
};

const validateCard = (file: File): string | null => {
	const mime = effectiveMime(file);
	if (!isImageFile(file) || !ALLOWED_CARD_MIME.includes(mime)) {
		return 'Use a photo of your voter’s card (JPG, PNG, HEIC, or WebP).';
	}
	if (file.size > MAX_IMAGE_SIZE) return 'Photo must be smaller than 8 MB.';
	return null;
};

const computeKycReview = (
	selfie: { reviewReasons: KYCReviewReason[] },
	card: { reviewReasons: KYCReviewReason[] }
): { kycReviewRequired: boolean; kycReviewReasons: KYCReviewReason[] } => {
	const reasons = [...selfie.reviewReasons, ...card.reviewReasons];
	return { kycReviewRequired: reasons.length > 0, kycReviewReasons: reasons };
};

const IdentityVerificationCard = <TFieldValues extends IdentityVerificationFields>({
	hookForm,
	actions,
	showVoterCard = true,
}: IIdentityVerificationCardProps<TFieldValues>) => {
	// `UseFormReturn` is invariant in its field type (its watch observer receives
	// `Path<T>`), so a superset form is not assignable to the subset the card
	// needs even though every field it touches is present with the right type —
	// the generic constraint above is what guarantees that. Narrow the handle
	// once here rather than widening the type of every `watch`/`setValue` call.
	const { watch, setValue } = hookForm as unknown as UseFormReturn<IdentityVerificationFields>;
	const { isMobile, isTouchDevice } = useIsMobile();

	const selfieFile = watch('profilePhoto') as File | undefined | null;
	const profileUrl = watch('profileUrl');
	const faceId = watch('faceId') as string | null | undefined;
	const cardFile = watch('votersCardDocument') as File | undefined | null;
	const cardImageUrl = watch('votersCardImage') as string | null | undefined;
	const typedVin = (watch('votersCardRegistrationNumber') as string | undefined) ?? '';

	// Slot status state machines
	const [selfieStatus, setSelfieStatus] = useState<SlotStatus>('idle');
	const [cardStatus, setCardStatus] = useState<SlotStatus>('idle');
	const [selfieMessage, setSelfieMessage] = useState<string>('');
	const [cardMessage, setCardMessage] = useState<string>('');
	const [selfieEnrol, setSelfieEnrol] = useState<IKYCFaceEnrolResponse | null>(null);
	const [cardCheck, setCardCheck] = useState<IKYCIdCheckResponse | null>(null);
	const [selfieReasons, setSelfieReasons] = useState<KYCReviewReason[]>([]);
	const [cardReasons, setCardReasons] = useState<KYCReviewReason[]>([]);
	const [vinReconcile, setVinReconcile] = useState<'match' | 'mismatch' | 'unread' | null>(null);
	// Holds a video file the user picked when we want to nudge them toward a still
	// photo (HEVC/MOV reliably rejected by the KYC service). When set, the slot
	// shows an inline callout with `Use a photo` / `Continue with video` actions.
	const [pendingHEVCVideo, setPendingHEVCVideo] = useState<File | null>(null);

	// Desktop-only in-browser camera modals. Mobile users still go through the
	// `<input capture>` path because the OS-native camera UI is strictly better
	// than anything we can build here (autofocus, flash, gallery toggle, etc.).
	const [selfieCameraOpen, setSelfieCameraOpen] = useState<boolean>(false);
	const [cardCameraOpen, setCardCameraOpen] = useState<boolean>(false);

	// File input refs
	const selfieCameraRef = useRef<HTMLInputElement>(null);
	const selfieGalleryRef = useRef<HTMLInputElement>(null);
	const selfiePhotoOnlyRef = useRef<HTMLInputElement>(null);
	const cardCameraRef = useRef<HTMLInputElement>(null);
	const cardGalleryRef = useRef<HTMLInputElement>(null);

	const selfieThumbUrl = useMemo(
		() => (selfieFile ? URL.createObjectURL(selfieFile) : (profileUrl ?? '')),
		[selfieFile, profileUrl]
	);
	const cardThumbUrl = useMemo(() => (cardFile ? URL.createObjectURL(cardFile) : (cardImageUrl ?? '')), [cardFile, cardImageUrl]);

	// Revoke the previous blob URL when the source file changes or the
	// component unmounts. Without this, every selfie replacement leaks a
	// few MB into memory on the user's phone.
	useEffect(() => {
		if (!selfieFile || !selfieThumbUrl?.startsWith('blob:')) return;
		return () => URL.revokeObjectURL(selfieThumbUrl);
	}, [selfieFile, selfieThumbUrl]);
	useEffect(() => {
		if (!cardFile || !cardThumbUrl?.startsWith('blob:')) return;
		return () => URL.revokeObjectURL(cardThumbUrl);
	}, [cardFile, cardThumbUrl]);

	// ---------- Helpers to write KYC review state into the form ----------

	const updateReviewFlags = (nextSelfie: KYCReviewReason[], nextCard: KYCReviewReason[]) => {
		const result = computeKycReview({ reviewReasons: nextSelfie }, { reviewReasons: nextCard });
		setValue('kycReviewRequired', result.kycReviewRequired, { shouldValidate: true, shouldDirty: true });
		setValue('kycReviewReasons', result.kycReviewReasons as string[], { shouldValidate: true, shouldDirty: true });
	};

	// ---------- Selfie verification flow ----------

	// Inner helper: upload the (already-validated, MIME-clean) file to media-manager
	// + KYC. Splits image vs video paths.
	const dispatchSelfieUpload = (file: File, video: boolean) => {
		setValue('profilePhoto', file, { shouldValidate: true, shouldDirty: true });
		setSelfieStatus('busy');
		setSelfieMessage(video ? 'Analysing your selfie video…' : 'Verifying liveness…');

		// 1) Upload still image to media-manager for the profilePhoto URI in the payload.
		//    Videos are skipped here — media-manager only stores images, and the
		//    backend's profilePhoto field expects an image URI. faceId is the
		//    source of identity trust either way.
		if (!video) {
			const fd = new FormData();
			fd.append('upload', file);
			actions?.uploadResultDataAction(
				fd,
				(res) => {
					const url = res?.data?.uri;
					if (url) setValue('profileUrl', url, { shouldValidate: true, shouldDirty: true });
				},
				() => {
					showToast('error', 'We couldn’t upload your selfie. Please try again.');
				}
			);
		}

		// 2) Run KYC face enrolment in parallel — video uses the multi-frame endpoint.
		const enrolAction = video ? actions?.kycEnrolFaceVideoAction : actions?.kycEnrolFaceAction;
		enrolAction?.(
			file,
			(res: IKYCFaceEnrolResponse) => {
				setSelfieEnrol(res);
				const reasons: KYCReviewReason[] = [];

				if (!res.has_face) reasons.push('no_face_detected');
				if (res.liveness_checked && !res.is_live) reasons.push('liveness_failed');

				// Hard threshold: any confidence below 90% is a reject.
				const confPct = toPercent(res.confidence);
				const lowConfidence = res.has_face && confPct != null && confPct < MIN_CONFIDENCE_PERCENT;
				if (lowConfidence) reasons.push('low_face_confidence');

				const acceptable = res.face_id && reasons.length === 0;

				if (acceptable) {
					setValue('faceId', res.face_id, { shouldValidate: true, shouldDirty: true });
					setSelfieStatus('success');
					setSelfieMessage(`Verified live human · ${confPct?.toFixed(0)}% confidence.`);
				} else {
					// Reject: clear any previously-stored face_id so the user must retry.
					setValue('faceId', null, { shouldValidate: true, shouldDirty: true });
					setSelfieStatus('failed');
					if (lowConfidence) {
						setSelfieMessage(
							`Liveness confidence ${confPct?.toFixed(0)}% is below the required ${MIN_CONFIDENCE_PERCENT}%. Try again in better lighting and look directly at the camera.`
						);
					} else {
						setSelfieMessage(res.message ?? 'We couldn’t verify your selfie. Try again with better lighting.');
					}
					if (reasons.length === 0) reasons.push('kyc_service_error');
				}

				setSelfieReasons(reasons);
				updateReviewFlags(reasons, cardReasons);
			},
			() => {
				setSelfieStatus('failed');
				setSelfieMessage('We couldn’t reach the verification service. You can retry or continue for review.');
				const reasons: KYCReviewReason[] = ['kyc_service_error'];
				setSelfieReasons(reasons);
				setValue('faceId', null, { shouldValidate: true, shouldDirty: true });
				updateReviewFlags(reasons, cardReasons);
			}
		);
	};

	// Outer entry: validate, normalise (HEIC→JPEG, EXIF rotate, compress, MIME repair),
	// then dispatch. Image and video paths diverge after normalisation.
	const handleSelfieFile = async (file: File) => {
		setPendingHEVCVideo(null);
		const err = validateSelfie(file);
		if (err) {
			showToast('error', err);
			return;
		}

		const video = isVideoFile(file);

		if (video) {
			const result = normaliseSelfieVideo(file);
			if (result.sizeError) {
				showToast('error', result.sizeError);
				return;
			}
			if (result.warnHEVC) {
				// Park the file and ask the user to confirm — still photos are
				// far more reliable through the KYC pipeline.
				setPendingHEVCVideo(result.file);
				setValue('profilePhoto', result.file, { shouldValidate: true, shouldDirty: true });
				setSelfieStatus('warning');
				setSelfieMessage('iPhone videos sometimes use a format the verifier can’t read. A still selfie is more reliable.');
				return;
			}
			dispatchSelfieUpload(result.file, true);
			return;
		}

		// Image path — normalise then dispatch.
		setSelfieStatus('converting');
		setSelfieMessage('Optimising your photo…');
		try {
			const normalised = await normaliseSelfieImage(file);
			dispatchSelfieUpload(normalised, false);
		} catch (e) {
			console.warn('Selfie normalisation failed; falling back to raw file', e);
			dispatchSelfieUpload(file, false);
		}
	};

	const continuePendingVideo = () => {
		if (!pendingHEVCVideo) return;
		const f = pendingHEVCVideo;
		setPendingHEVCVideo(null);
		dispatchSelfieUpload(f, true);
	};

	const switchToPhotoFromPendingVideo = () => {
		setPendingHEVCVideo(null);
		setSelfieStatus('idle');
		setSelfieMessage('');
		setValue('profilePhoto', null, { shouldValidate: true, shouldDirty: true });
		// Open a photo-only file picker so the user doesn't accidentally pick another video.
		selfiePhotoOnlyRef.current?.click();
	};

	// ---------- Voter card verification flow ----------

	const reconcileVin = (ocr: IKYCIdCheckResponse): { reasons: KYCReviewReason[]; reconcile: typeof vinReconcile } => {
		const reasons: KYCReviewReason[] = [];
		const v = ocr.validation;
		if (!v.card_detected) reasons.push('card_not_detected');
		if (v.card_detected && !v.is_valid_document) reasons.push('invalid_card_document');

		// Hard threshold: card-detection confidence below 90% is a reject.
		const validationPct = toPercent(v.confidence);
		if (v.card_detected && validationPct != null && validationPct < MIN_CONFIDENCE_PERCENT) {
			reasons.push('low_card_confidence');
		}

		// Validate the VIN ourselves on the normalised value — the service's
		// `vin_format_valid` flag is unreliable (it fails on the spaces it
		// itself prints into the VIN).
		const ocrVin = normaliseVin(ocr.ocr.vin);

		if (!ocrVin) {
			reasons.push('vin_ocr_failed');
			return { reasons, reconcile: 'unread' };
		}
		if (!isVinFormatValid(ocrVin)) {
			reasons.push('vin_format_invalid');
			return { reasons, reconcile: 'unread' };
		}

		// Hard threshold: OCR confidence below 90% is a reject (even with a parseable VIN).
		const ocrPct = toPercent(ocr.ocr.raw_confidence);
		if (ocrPct != null && ocrPct < MIN_CONFIDENCE_PERCENT) {
			reasons.push('low_ocr_confidence');
		}

		const typedNorm = normaliseVin(typedVin);
		if (typedNorm && typedNorm !== ocrVin) {
			reasons.push('vin_mismatch');
			return { reasons, reconcile: 'mismatch' };
		}
		return { reasons, reconcile: 'match' };
	};

	const dispatchCardUpload = (file: File) => {
		setValue('votersCardDocument', file, { shouldValidate: true, shouldDirty: true });
		setCardStatus('busy');
		setCardMessage('Reading your voter’s card…');

		// 1) Upload to media-manager
		const fd = new FormData();
		fd.append('upload', file);
		actions?.uploadResultDataAction(
			fd,
			(res) => {
				const url = res?.data?.uri;
				if (url) setValue('votersCardImage', url, { shouldValidate: true, shouldDirty: true });
			},
			() => {
				showToast('error', 'We couldn’t upload your voter card image.');
			}
		);

		// 2) Run KYC validation + OCR
		actions?.kycCheckIdAction(
			file,
			(res: IKYCIdCheckResponse) => {
				setCardCheck(res);
				const { reasons, reconcile } = reconcileVin(res);
				setCardReasons(reasons);
				setVinReconcile(reconcile);

				const hardFail = reasons.some((r) => ['card_not_detected', 'invalid_card_document'].includes(r));
				const lowConfidence = reasons.some((r) => ['low_card_confidence', 'low_ocr_confidence'].includes(r));

				if (reasons.length === 0) {
					setCardStatus('success');
					// We read the VIN automatically — populate the field if the user left it blank.
					if (!normaliseVin(typedVin)) {
						setValue('votersCardRegistrationNumber', normaliseVin(res.ocr.vin), { shouldValidate: true, shouldDirty: true });
					}
					const ocrPct = toPercent(res.ocr.raw_confidence);
					setCardMessage(`Voter’s card verified${ocrPct != null ? ` · ${ocrPct.toFixed(0)}% OCR confidence` : ''}.`);
				} else if (hardFail) {
					// Reject: clear the stored URI so the user must retry with a clearer image.
					setValue('votersCardImage', null, { shouldValidate: true, shouldDirty: true });
					setCardStatus('failed');
					setCardMessage('That doesn’t look like a valid voter’s card. Try a clearer photo.');
				} else if (lowConfidence) {
					// Preserve image + VIN; flag for manual review so the user can continue.
					setCardStatus('warning');
					const cp = toPercent(res.validation.confidence);
					const op = toPercent(res.ocr.raw_confidence);
					setCardMessage(
						`Card accepted for manual review${cp != null ? ` · detection ${cp.toFixed(0)}%` : ''}${op != null ? ` · OCR ${op.toFixed(0)}%` : ''}. You can continue — we’ll verify this manually.`
					);
				} else {
					setCardStatus('warning');
					setCardMessage(res.message ?? 'Card uploaded; please confirm the VIN below.');
				}

				updateReviewFlags(selfieReasons, reasons);
			},
			() => {
				setCardStatus('failed');
				setCardMessage('We couldn’t reach the verification service. You can retry or continue for review.');
				const reasons: KYCReviewReason[] = ['kyc_service_error'];
				setCardReasons(reasons);
				updateReviewFlags(selfieReasons, reasons);
			}
		);
	};

	const handleCardFile = async (file: File) => {
		const err = validateCard(file);
		if (err) {
			showToast('error', err);
			return;
		}

		setCardStatus('converting');
		setCardMessage('Optimising your card photo…');
		try {
			const normalised = await normaliseCardImage(file);
			dispatchCardUpload(normalised);
		} catch (e) {
			console.warn('Voter card normalisation failed; falling back to raw file', e);
			dispatchCardUpload(file);
		}
	};

	// ---------- Click handlers ----------

	// Split capture vs upload — primary opens the camera, secondary opens
	// the gallery / file picker. Both feed the same handleSelfieFile path.
	//
	// On touch devices (phones/tablets) we delegate to the native camera via
	// `<input capture>` — that gives the front camera for selfie and rear
	// camera for card with all OS affordances baked in. On non-touch devices
	// (laptops/desktops) we open an in-browser modal that streams from
	// `navigator.mediaDevices.getUserMedia()` since `capture` is silently
	// ignored on desktop browsers.
	const onSelfieCamera = () => (isTouchDevice ? selfieCameraRef.current?.click() : setSelfieCameraOpen(true));
	const onSelfieUpload = () => selfieGalleryRef.current?.click();
	const onCardCamera = () => (isTouchDevice ? cardCameraRef.current?.click() : setCardCameraOpen(true));
	const onCardUpload = () => cardGalleryRef.current?.click();

	const onSelfieInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (f) handleSelfieFile(f);
		e.target.value = '';
	};

	const onCardInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (f) handleCardFile(f);
		e.target.value = '';
	};

	const onCardDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		const f = e.dataTransfer.files?.[0];
		if (f) handleCardFile(f);
	};

	// ---------- Reconcile actions ----------

	const acceptOcrVin = () => {
		const ocrVin = normaliseVin(cardCheck?.ocr?.vin);
		if (!ocrVin) return;
		setValue('votersCardRegistrationNumber', ocrVin, { shouldValidate: true, shouldDirty: true });
		const nextReasons = cardReasons.filter((r) => r !== 'vin_mismatch');
		setCardReasons(nextReasons);
		setVinReconcile('match');
		setCardStatus(nextReasons.length === 0 ? 'success' : 'warning');
		if (nextReasons.length === 0) setCardMessage('Voter’s card verified.');
		updateReviewFlags(selfieReasons, nextReasons);
	};

	const keepTypedVin = () => {
		// User overrides — leave reasons as-is so kycReviewRequired stays true.
		setVinReconcile('mismatch');
	};

	const skipCard = () => {
		setValue('votersCardDocument', null, { shouldValidate: true, shouldDirty: true });
		setValue('votersCardImage', null, { shouldValidate: true, shouldDirty: true });
		setCardCheck(null);
		setCardStatus('idle');
		setCardMessage('');
		setCardReasons([]);
		setVinReconcile(null);
		updateReviewFlags(selfieReasons, []);
	};

	// ---------- Render helpers ----------

	const SelfieAction = () => {
		if (selfieStatus === 'converting') {
			return (
				<SlotActionButton
					variant='ghost'
					disabled
					type='button'
				>
					<SpinnerIcon size={16} />
					Optimising…
				</SlotActionButton>
			);
		}
		if (selfieStatus === 'busy') {
			return (
				<SlotActionButton
					variant='ghost'
					disabled
					type='button'
				>
					<SpinnerIcon size={16} />
					Verifying…
				</SlotActionButton>
			);
		}
		if (selfieStatus === 'failed') {
			return (
				<SlotActionRow>
					<SlotActionButton
						variant='danger'
						type='button'
						onClick={onSelfieCamera}
					>
						<RetryIcon size={16} />
						Retry
					</SlotActionButton>
					<SlotActionButton
						variant='ghost'
						type='button'
						onClick={onSelfieUpload}
					>
						<UploadIcon size={16} />
						Upload
					</SlotActionButton>
				</SlotActionRow>
			);
		}
		if (selfieStatus === 'success' || selfieStatus === 'warning') {
			return (
				<SlotActionButton
					variant='ghost'
					type='button'
					onClick={onSelfieCamera}
				>
					<ReplaceIcon size={16} />
					Replace photo
				</SlotActionButton>
			);
		}
		return (
			<SlotActionRow>
				<SlotActionButton
					variant='primary'
					type='button'
					onClick={onSelfieCamera}
				>
					<CameraIcon size={16} />
					Take selfie
				</SlotActionButton>
				<SlotActionButton
					variant='ghost'
					type='button'
					onClick={onSelfieUpload}
				>
					<UploadIcon size={16} />
					Upload
				</SlotActionButton>
			</SlotActionRow>
		);
	};

	const CardAction = () => {
		if (cardStatus === 'converting') {
			return (
				<SlotActionButton
					variant='ghost'
					disabled
					type='button'
				>
					<SpinnerIcon size={16} />
					Optimising…
				</SlotActionButton>
			);
		}
		if (cardStatus === 'busy') {
			return (
				<SlotActionButton
					variant='ghost'
					disabled
					type='button'
				>
					<SpinnerIcon size={16} />
					Verifying…
				</SlotActionButton>
			);
		}
		if (cardStatus === 'failed') {
			return (
				<SlotActionRow>
					<SlotActionButton
						variant='danger'
						type='button'
						onClick={onCardCamera}
					>
						<RetryIcon size={16} />
						Retry
					</SlotActionButton>
					<SlotActionButton
						variant='ghost'
						type='button'
						onClick={onCardUpload}
					>
						<UploadIcon size={16} />
						Upload
					</SlotActionButton>
				</SlotActionRow>
			);
		}
		if (cardStatus === 'success' || cardStatus === 'warning') {
			return (
				<SlotActionButton
					variant='ghost'
					type='button'
					onClick={onCardCamera}
				>
					<ReplaceIcon size={16} />
					Replace card
				</SlotActionButton>
			);
		}
		return (
			<SlotActionRow>
				<SlotActionButton
					variant='primary'
					type='button'
					onClick={onCardCamera}
				>
					<ScanCardIcon size={16} />
					Scan card
				</SlotActionButton>
				<SlotActionButton
					variant='ghost'
					type='button'
					onClick={onCardUpload}
				>
					<UploadIcon size={16} />
					Upload
				</SlotActionButton>
			</SlotActionRow>
		);
	};

	return (
		<VerificationCard>
			<VerificationHeader>
				<div className='titleblock'>
					<p className='eyebrow'>Identity verification</p>
					<h3 className='title'>Prove you’re a real, registered voter</h3>
				</div>
				<span className='progressbadge'>Step 1 of 4</span>
			</VerificationHeader>

			{/* SELFIE SLOT */}
			<VerificationSlot status={selfieStatus}>
				<div className='thumbWrap'>
					<SlotThumb className='circle'>
						{selfieThumbUrl ? (
							<>
								{selfieFile && isVideoFile(selfieFile) ? (
									<video
										src={selfieThumbUrl}
										muted
										playsInline
										autoPlay
										loop
										style={{ width: '100%', height: '100%', objectFit: 'cover' }}
									/>
								) : (
									<img
										src={selfieThumbUrl}
										alt='Selfie preview'
									/>
								)}
								{selfieStatus === 'success' && (
									<span
										className='checkOverlay'
										aria-label='Verified'
									>
										<CheckIcon
											size={14}
											strokeWidth={2.5}
										/>
									</span>
								)}
							</>
						) : selfieStatus === 'busy' || selfieStatus === 'converting' ? (
							<SlotSpinner />
						) : (
							<FaceSilhouettePlaceholder size={42} />
						)}
					</SlotThumb>
				</div>

				<div className='info'>
					<p className='infoTitle'>Take a clear selfie or short video</p>
					<p className='infoSubtitle'>
						{selfieMessage || (selfieStatus === 'idle' ? 'A short video gives the strongest liveness signal.' : '')}
					</p>
					{selfieStatus === 'success' && selfieEnrol?.confidence != null && (
						<ResultChip tone='success'>Live human · {toPercent(selfieEnrol.confidence)?.toFixed(0)}% confidence</ResultChip>
					)}
					{selfieStatus === 'warning' && <ResultChip tone='warning'>Saved with warnings · review needed</ResultChip>}
					{selfieStatus === 'failed' && faceId == null && <ResultChip tone='danger'>Verification didn’t pass</ResultChip>}
				</div>

				<div className='actionWrap'>
					<SelfieAction />
				</div>

				{pendingHEVCVideo && (
					<ReconcileCallout>
						<div>
							<strong>Photos work most reliably.</strong> iPhone videos sometimes use a format the verifier can’t read. Want to
							try a still selfie instead?
						</div>
						<div className='actions'>
							<button
								className='actionBtn primary'
								type='button'
								onClick={switchToPhotoFromPendingVideo}
							>
								Use a photo
							</button>
							<button
								className='actionBtn'
								type='button'
								onClick={continuePendingVideo}
							>
								Continue with video
							</button>
						</div>
					</ReconcileCallout>
				)}

				{/* hidden inputs — accept image OR short video for liveness */}
				<input
					ref={selfieCameraRef}
					type='file'
					accept={SELFIE_CAMERA_ACCEPT}
					capture='user'
					onChange={onSelfieInputChange}
					style={{ display: 'none' }}
				/>
				<input
					ref={selfieGalleryRef}
					type='file'
					accept={SELFIE_GALLERY_ACCEPT}
					onChange={onSelfieInputChange}
					style={{ display: 'none' }}
				/>
				{/* Image-only fallback used when the user opts to switch from a HEVC video. */}
				<input
					ref={selfiePhotoOnlyRef}
					type='file'
					accept={SELFIE_PHOTO_ONLY_ACCEPT}
					capture='user'
					onChange={onSelfieInputChange}
					style={{ display: 'none' }}
				/>
			</VerificationSlot>

			{/* VOTER CARD SLOT — hidden for observers, who don't submit a voter's card */}
			{showVoterCard && (
				<VerificationSlot
					status={cardStatus}
					onDrop={!isMobile ? onCardDrop : undefined}
					onDragOver={!isMobile ? (e: DragEvent<HTMLDivElement>) => e.preventDefault() : undefined}
				>
					<div className='thumbWrap'>
						<SlotThumb>
							{cardThumbUrl && cardFile?.type !== 'application/pdf' ? (
								<>
									<img
										src={cardThumbUrl}
										alt='Voter card preview'
									/>
									{cardStatus === 'success' && (
										<span
											className='checkOverlay'
											aria-label='Verified'
										>
											<CheckIcon
												size={14}
												strokeWidth={2.5}
											/>
										</span>
									)}
								</>
							) : cardStatus === 'busy' || cardStatus === 'converting' ? (
								<SlotSpinner />
							) : (
								<VoterCardPlaceholder size={42} />
							)}
						</SlotThumb>
					</div>

					<div className='info'>
						<p className='infoTitle'>Photograph your voter’s card (optional)</p>
						<p className='infoSubtitle'>
							{cardMessage || (cardStatus === 'idle' ? 'We’ll read the VIN automatically. Skip if you don’t have it handy.' : '')}
						</p>
						{cardStatus === 'success' && cardCheck?.ocr?.vin && (
							<ResultChip tone='success'>VIN extracted · {cardCheck.ocr.vin}</ResultChip>
						)}
						{cardStatus === 'warning' && <ResultChip tone='warning'>Card saved · review needed</ResultChip>}
						{cardStatus === 'failed' && <ResultChip tone='danger'>{cardMessage || 'Verification didn’t pass'}</ResultChip>}
						{cardStatus === 'idle' && (
							<SkipLink
								type='button'
								onClick={skipCard}
							>
								Skip — I’ll only enter the VIN
							</SkipLink>
						)}
					</div>

					<div className='actionWrap'>
						<CardAction />
					</div>

					{vinReconcile === 'mismatch' && cardCheck?.ocr?.vin && (
						<ReconcileCallout>
							<div>
								The VIN we read from the card is <strong>{cardCheck.ocr.vin}</strong>, but you typed{' '}
								<strong>{typedVin || '—'}</strong>. Which value should we keep?
							</div>
							<div className='actions'>
								<button
									className='actionBtn primary'
									type='button'
									onClick={acceptOcrVin}
								>
									Use the OCR value
								</button>
								<button
									className='actionBtn'
									type='button'
									onClick={keepTypedVin}
								>
									Keep what I typed
								</button>
							</div>
						</ReconcileCallout>
					)}
					{vinReconcile === 'unread' && (
						<ReconcileCallout>
							We couldn’t read the VIN clearly. You can retake the photo or keep the value you typed below.
							<div className='actions'>
								<button
									className='actionBtn primary'
									type='button'
									onClick={onCardCamera}
								>
									Retake photo
								</button>
							</div>
						</ReconcileCallout>
					)}

					{/* hidden inputs */}
					<input
						ref={cardCameraRef}
						type='file'
						accept='image/*'
						capture='environment'
						onChange={onCardInputChange}
						style={{ display: 'none' }}
					/>
					<input
						ref={cardGalleryRef}
						type='file'
						accept={CARD_ACCEPT}
						onChange={onCardInputChange}
						style={{ display: 'none' }}
					/>
				</VerificationSlot>
			)}

			<FooterNote>
				<span aria-hidden>🔒</span>
				Your photos are encrypted in transit and used only for identity verification.
			</FooterNote>

			{/* Desktop-only in-browser camera modals. Mobile users continue to use the
			    native camera via the `<input capture>` paths above. */}
			<CameraCaptureModal
				open={selfieCameraOpen}
				mode='selfie'
				onClose={() => setSelfieCameraOpen(false)}
				onCapture={(file) => handleSelfieFile(file)}
				onUploadFallback={onSelfieUpload}
			/>
			<CameraCaptureModal
				open={cardCameraOpen}
				mode='card'
				onClose={() => setCardCameraOpen(false)}
				onCapture={(file) => handleCardFile(file)}
				onUploadFallback={onCardUpload}
			/>
		</VerificationCard>
	);
};

export default IdentityVerificationCard;
